import { usePaginatedQuery, useMutation as useConvexMutation, useQuery } from "convex/react";
import { api } from "../../api";
import { useToast } from "@/hooks/use-toast";
import { useState, useCallback, useMemo } from "react";
import { type GenericId as Id } from "convex/values";
import { getDeviceStatus, type DeviceStatus } from "@/utils/deviceStatus";
import { useCurrentUser } from "@/hooks/useCurrentUser";

interface ConvexDevice {
  id: string;
  deviceConfig: Id<"deviceConfigs">;
  jssid?: number;
  model?: string;
  serial?: string;
  profile?: number;
  user?: {
    email?: string;
    firstName: string;
    lastName: string;
    phone?: string;
  };
}

interface Device {
  id: string;
  name: string;
  user: string;
  os: string;
  version: string;
  status: DeviceStatus;
  lastSync: string;
  group: string;
  serial?: string;
  jssId?: number;
  profileId?: number;
  hasProfile?: boolean;
  level?: {
    id: string;
    name: string;
  };
  user_info?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    device_management_type: "self_managed" | "chaver_managed" | "org_managed";
  };
}

// Helper function to generate device name
const generateDeviceName = (convexDevice: ConvexDevice): string => {
  if (convexDevice.serial) {
    return convexDevice.serial;
  }
  return `Device ${convexDevice.jssid || 'Unknown'}`;
};

// Helper function to format user name from user object
const formatUserName = (user?: ConvexDevice['user']): string => {
  if (!user) {
    return "Unassigned";
  }
  
  const { firstName, lastName, email } = user;
  
  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  }
  
  if (firstName) {
    return firstName;
  }
  
  if (lastName) {
    return lastName;
  }
  
  if (email) {
    return email;
  }
  
  return "Unknown User";
};

// Helper function to transform device data
const transformDevice = (convexDevice: ConvexDevice): Device => {
  const deviceName = generateDeviceName(convexDevice);
  const deviceStatus = getDeviceStatus(convexDevice.serial, convexDevice.jssid);
  const userName = formatUserName(convexDevice.user);
  // For active devices, check if they have a profile
  const hasProfile = deviceStatus === 'active' ? !!convexDevice.profile : undefined;

  return {
    id: convexDevice.id,
    name: deviceName,
    user: userName,
    os: "iOS",
    version: "17.0",
    status: deviceStatus,
    lastSync: "Never",
    group: "Default Group",
    serial: convexDevice.serial,
    jssId: convexDevice.jssid,
    profileId: convexDevice.profile,
    hasProfile,
  };
};

export const useConvexDevices = () => {
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState("");

  // Get current user role - handle unauthenticated case
  const { user, isAuthenticated } = useCurrentUser();
  const userRole = user?.roles?.[0]?._id;

  // Use error boundary approach to handle the Promise serialization issue
  let devicesQuery: any;
  try {
    devicesQuery = usePaginatedQuery(
      api.devices.list.all, 
      isAuthenticated ? {} : "skip",
      { initialNumItems: 50 }
    );
  } catch (error) {
    console.error("Error with devices query:", error);
    devicesQuery = { results: [], isLoading: false, status: "LoadingFirstPage", loadMore: () => {} };
  }

  // Transform the data with error handling
  const devices: Device[] = useMemo(() => {
    if (!devicesQuery.results) return [];
    
    try {
      // Filter out any null/undefined results that might be promises
      const validResults = devicesQuery.results.filter((result: any) => 
        result && typeof result === 'object' && 'id' in result
      );
      return validResults.map(transformDevice);
    } catch (error) {
      console.error("Error transforming device data:", error);
      return [];
    }
  }, [devicesQuery.results]);

  const hasMore = devicesQuery.status === "CanLoadMore";
  const isLoading = devicesQuery.status === "LoadingFirstPage";
  const isLoadingMore = devicesQuery.status === "LoadingMore";

  // Load more function for pagination
  const loadMore = useCallback(() => {
    if (hasMore && devicesQuery.loadMore) {
      devicesQuery.loadMore(50);
    }
  }, [hasMore, devicesQuery.loadMore]);

  // Update device level mutation
  const updateDeviceLevelMutation = useConvexMutation(api.devices.level.update);

  const updateDeviceLevel = useCallback(async (deviceId: string, levelId: string) => {
    try {
      await updateDeviceLevelMutation({
        device: deviceId as any,
        level: levelId as any
      });
      
      toast({
        title: "Success",
        description: "Device level updated successfully",
      });
    } catch (error) {
      console.error("Error updating device level:", error);
      toast({
        title: "Error",
        description: "Failed to update device level",
        variant: "destructive",
      });
      throw error;
    }
  }, [updateDeviceLevelMutation, toast]);

  const getDevice = useCallback((deviceId: string) => {
    return devices.find(d => d.id === deviceId);
  }, [devices]);

  return {
    devices,
    isLoading,
    isLoadingMore,
    hasMore,
    loadMore,
    searchTerm,
    setSearchTerm,
    updateDeviceLevel,
    getDevice,
    totalCount: devices.length
  };
};
